import React, { useEffect, useState, useContext } from 'react';
import {
  Table,
  Button,
  Select,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  Dropdown,
  Menu,
  Transfer,
  Card,
} from 'antd';
import { EditTwoTone, DeleteTwoTone } from '@ant-design/icons';
import {
  getClasses,
  FilterClassesByInstitutionId,
  getTeachers,
  getClassStudents,
  deleteClass,
  getUnBindStudents,
  bindStudentToClass,
  unBindStudentToClass,
} from '../../../actions';
import { Context } from '../../../defaults';
import { ClassesModal } from './ClassesModal';
import './index.css';

function ClassesTable({ added, institutions, setAdded }) {
  const [classes, setClasses] = useState();
  const [teachers, setTeachers] = useState();
  const [selectedInstitution, setSelectedInstitution] = useState();
  const [selectedClass, setSelectedClass] = useState();
  const [isStudentChange, setIsStudentChange] = useState(false);
  const [bindedStudents, setBindedStudents] = useState([]);
  const [unBindStudents, setUnBindStudents] = useState([]);

  const { dictionary, language } = useContext(Context);

  const [modalProps, setModalProps] = useState({
    visible: false,
    actionType: null,
    currentClass: null,
  });

  const confirm = (id) => {
    deleteClass(id).then((res) => {
      if (selectedInstitution) {
        FilterClassesByInstitutionId(selectedInstitution).then(({ data }) => setClasses(data));
      }
    });
  };

  useEffect(() => {
    if (selectedInstitution) {
      FilterClassesByInstitutionId(selectedInstitution).then(({ data }) => setClasses(data));
      getTeachers().then(({ data }) => setTeachers(data));
    }
  }, [selectedInstitution]);

  useEffect(() => {
    setAdded(false);
    setSelectedInstitution('');
    setSelectedClass('');
  }, [added]);

  useEffect(() => {
    if (selectedClass) {
      getClassStudents(selectedInstitution, selectedClass).then((res) => {
        getUnBindStudents(selectedInstitution).then((r) => {
          setBindedStudents([
            ...res.data.map((el) => {
              return {
                key: el.id,
                value: el.id,
                description: `${el.lastName} ${el.firstName} ${el.middleName}`,
              };
            }),
            ...r.data.map((el) => {
              return {
                key: el.id,
                value: el.id,
                description: `${el.lastName} ${el.firstName} ${el.middleName}`,
              };
            }),
          ]);
          setUnBindStudents(r.data.map((el) => el.id));
        });
      });
    }
  }, [selectedClass]);

  const columns = [
    {
      title: `${dictionary?.Year[language]}`,
      dataIndex: 'year',
      key: 'year',
      width: 200,
    },
    {
      title: `${dictionary?.Letter[language]}`,
      dataIndex: 'letter',
      key: 'letter',
      width: 200,
    },
    {
      title: `${dictionary?.Teachers[language]}`,
      dataIndex: 'id',
      key: 'classId',
      render: (id) => {
        if (teachers) {
          const teacher = teachers.find((el) => el.classId === id);
          if (teacher) {
            return `${teacher.lastName} ${teacher.firstName} ${teacher.middleName}`;
          }
        }
        return '';
      },
      width: 200,
    },
    {
      title: `${dictionary?.Action[language]}`,
      key: 'action',
      width: 200,
      fixed: 'right',
      render: ({ id }) => (
        <Row gutter={8}>
          <Col span={6}>
            <Tooltip placement="top" title={dictionary?.Edit[language]}>
              <EditTwoTone
                onClick={() => {
                  setModalProps({
                    visible: !modalProps.visible,
                    actionType: 'edit',
                    currentClass: classes && classes.find((el) => el.id === id),
                  });
                }}
              />
            </Tooltip>
          </Col>
          <Col span={6}>
            <Tooltip placement="top" title={dictionary?.Delete[language]}>
              <Popconfirm
                placement="bottom"
                title={dictionary?.AreYouSureToDelete[language]}
                onConfirm={() => confirm(id)}
                okText={dictionary?.Yes[language]}
                cancelText={dictionary?.No[language]}>
                <DeleteTwoTone key="delete" twoToneColor="#eb2f96" />
              </Popconfirm>
            </Tooltip>
          </Col>
          <Col span={12}>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    onClick={() => {
                      setSelectedClass(id);
                      setIsStudentChange(true);
                    }}>
                    {dictionary?.ChangeStudentsList[language]}
                  </Menu.Item>
                </Menu>
              }
              placement="bottomLeft">
              <Button>{dictionary?.More[language]}</Button>
            </Dropdown>
          </Col>
        </Row>
      ),
    },
  ];

  const closeModal = () => {
    setModalProps({ visible: false });
    if (selectedInstitution) {
      FilterClassesByInstitutionId(selectedInstitution).then(({ data }) => setClasses(data));
      getTeachers().then(({ data }) => setTeachers(data));
    }
  };

  const classTitle = classes?.filter((x) => x.id === selectedClass);

  const handleChange = (targetKeys, direction, moveKeys) => {
    if (direction === 'right') {
      unBindStudentToClass(moveKeys).then((res) => console.log(res));
    } else {
      bindStudentToClass(selectedClass, moveKeys).then((res) => console.log(res));
    }
    setUnBindStudents([...targetKeys]);
  };

  const renderItem = (item) => {
    const customLabel = <span>{item.description}</span>;

    return {
      label: customLabel,
      value: item.value,
    };
  };

  return (
    <React.Fragment>
      <ClassesModal
        modalProps={modalProps}
        teachers={teachers?.filter((x) => x.institutionId === selectedInstitution)}
        closeModal={closeModal}
      />
      <Row gutter={16}>
        <Col span={24}>
          <Select
            placeholder={dictionary?.Institution[language]}
            allowClear={true}
            showSearch
            style={{ width: '100%' }}
            onChange={(value) => setSelectedInstitution(value)}
            value={selectedInstitution}
            size="large"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }>
            {institutions &&
              institutions.map((el) => {
                return (
                  <Select.Option value={el.id} key={`school${el.id}`}>
                    {el.name}
                  </Select.Option>
                );
              })}
          </Select>
        </Col>
      </Row>
      {selectedInstitution && (
        <Table style={{ marginTop: 24 }} columns={columns} dataSource={classes} rowKey="id" />
      )}
      {selectedClass && isStudentChange && (
        <Card
          title={`${dictionary?.ChangeStudentsList[language]}. ${dictionary.Class[language]} - ${classTitle[0].year} ${classTitle[0].letter}`}
          style={{ width: '100%' }}>
          <Transfer
            dataSource={bindedStudents}
            titles={[dictionary?.Binded[language], dictionary?.Unbind[language]]}
            listStyle={{
              width: 450,
              height: 400,
            }}
            targetKeys={unBindStudents}
            onChange={handleChange}
            render={renderItem}
          />
        </Card>
      )}
    </React.Fragment>
  );
}

export default ClassesTable;
